/** Versioned, original D artwork. Rendering never changes simulation or save data. */
export const INK_EXPORT = {
  cell: 288,
  pivot: { x: 144, y: 212 },
  units: 160,
} as const;
export const INK_ACTORS = [
  "hero",
  "pumpkin",
  "coconut",
  "wintermelon",
  "banana",
  "moonherb",
  "carrot",
  "chili",
  "mite",
  "brute",
  "spitter",
  "sentinel",
  "moth",
  "thorn-warden",
  "ember-tortoise",
] as const;
export const BOTANICAL_PARTS = [
  "front",
  "side",
  "back",
  "upper",
  "lower",
  "thigh",
  "boot",
  "tool",
] as const;
export const CROP_PARTS = ["seedling", "young", "mature", "leaf"] as const;
export const INK_PARTS = {
  hero: [
    "head-front",
    "head-back",
    "body-front",
    "body-back",
    "upper-arm",
    "forearm",
    "thigh",
    "boot",
    "staff",
    "axe",
    "pick",
    "can",
    "scarf",
    "head-side",
    "body-side",
    "hand",
  ],
  pumpkin: [
    "head-front",
    "head-back",
    "body-front",
    "body-back",
    "upper-arm",
    "forearm",
    "thigh",
    "boot",
    "axe",
    "head-side",
    "body-side",
    "leaf",
    "beetle-shell",
    "beetle-head",
    "beetle-upper",
    "beetle-lower",
  ],
  props: [
    "tree",
    "stump",
    "rock",
    "spent-rock",
    "amber",
    "herb",
    "berry",
    "fern",
    "well",
    "gate",
    "camp",
    "log",
    "bed",
    "crop-pumpkin",
    "crop-carrot",
    "cliff",
  ],
} as const;
type ArtImage = CanvasImageSource & { width: number; height: number };
const assets = new Map<string, ArtImage>();
let requested = false;
export let inkRevision = 0;
export function setInkAsset(id: string, image: ArtImage) {
  assets.set(id, image);
  inkRevision++;
}
export function inkAsset(id: string) {
  return assets.get(id);
}
export function loadInkAssets() {
  if (requested) return;
  requested = true;
  for (const part of [
    "hero-front",
    "hero-side",
    "hero-back",
    "hero-scarf",
    "moonherb-front",
    "moonherb-side",
    "moonherb-back",
    "moonherb-petal",
    "pumpkin-thigh",
    "pumpkin-boot",
    "pumpkin-front",
    "pumpkin-side",
    "sentinel-spear",
    "carrot-bow",
    "hero-thigh",
    "hero-boot",
  ]) {
    const im = new Image();
    im.decoding = "async";
    im.onload = () => setInkAsset(`v8/clothing/${part}`, im);
    im.src = `/art/ink-v8/clothing/${part}.webp`;
  }
  for (const id of ["garden", "grove", "quarry"]) {
    const im = new Image();
    im.decoding = "async";
    im.onload = () => setInkAsset(`island-${id}`, im);
    im.src = `/art/ink-v8/island-${id}.webp`;
  }
  for (const part of CROP_PARTS) {
    const im = new Image();
    im.decoding = "async";
    im.onload = () => setInkAsset(`v8/decor/${part}`, im);
    im.src = `/art/ink-v8/decor/${part}.webp`;
  }
  for (const group of [...INK_ACTORS, "shorekit"])
    for (const part of BOTANICAL_PARTS) {
      const im = new Image();
      im.decoding = "async";
      im.onload = () => setInkAsset(`v8/${group}/${part}`, im);
      im.src = `/art/ink-v8/${group}/${part}.webp`;
    }
  for (const id of INK_ACTORS.slice(1, 8))
    for (const part of CROP_PARTS) {
      const im = new Image();
      im.decoding = "async";
      im.onload = () => setInkAsset(`v8/crop-${id}/${part}`, im);
      im.src = `/art/ink-v8/crop-${id}/${part}.webp`;
    }
  for (const [group, names] of Object.entries(INK_PARTS))
    for (const name of names) {
      const im = new Image();
      im.decoding = "async";
      im.onload = () => setInkAsset(`${group}/${name}`, im);
      im.src = `/art/ink-v7/${group}/${name}.webp`;
    }
  for (const name of ["terrain", "sky", "bridge"]) {
    const im = new Image();
    im.decoding = "async";
    im.onload = () => setInkAsset(name, im);
    im.src = `/art/ink-v7/${name}.webp`;
  }
}
/** Constant authored size per part: no animation-dependent image scaling. */
export function inkPart(
  ctx: CanvasRenderingContext2D,
  id: string,
  x: number,
  y: number,
  height: number,
  px = 0.5,
  py = 1,
  angle = 0,
) {
  const im = assets.get(id);
  if (!im) return false;
  const width = (height * im.width) / im.height;
  ctx.save();
  ctx.translate(x, y);
  ctx.rotate(angle);
  ctx.drawImage(im, -width * px, -height * py, width, height);
  ctx.restore();
  return true;
}
